import React from 'react'

import { CreateOneOnOneSetupModal } from '@/components/modals/CreateOneOnOneSetupModal'

type CreateSetupModalProps = React.ComponentProps<typeof CreateOneOnOneSetupModal>
type TeamMember = CreateSetupModalProps['teamMembers'][number]

interface OneOnOneSetup {
  id: string
  title?: string
  participantId: string
  managerId: string
  workflowType?: 'email' | 'physical'
  frequency: string
  duration: number
  questions?: Array<{
    id?: string
    text?: string
    type?: string
    questionType?: string
    options?: string[]
    isRequired?: boolean
    order?: number
  }>
  defaultAgenda: string[]
  isActive: boolean
  schedulePattern?: string | null
  startDate?: string | null
  startTime?: string | null
  customCron?: string | null
  nextScheduledDate?: string | null
  emailDueDays?: number | null
  manager?: any
  participant?: any
}

interface EditOneOnOneSetupModalProps {
  isOpen: boolean
  onClose: () => void
  teamId: string
  setup: OneOnOneSetup | null
  teamMembers: TeamMember[]
  onSuccess?: () => void
}

export function EditOneOnOneSetupModal({
  isOpen,
  onClose,
  teamId,
  setup,
  teamMembers,
  onSuccess,
}: EditOneOnOneSetupModalProps) {
  const initialSetup = React.useMemo(() => {
    if (!setup) return null

    const deriveProfile = (entity: any) => {
      if (!entity) return null
      const user = entity.user ?? entity
      if (!user?.id) return null
      const firstName = user.firstName ?? user.first_name ?? ''
      const lastName = user.lastName ?? user.last_name ?? ''
      const fullName = user.fullName ?? user.full_name ?? `${firstName} ${lastName}`.trim()
      return {
        id: user.id,
        firstName,
        lastName,
        fullName: fullName || firstName || lastName || user.email || 'Member',
        email: user.email ?? null,
      }
    }

    return {
      id: setup.id,
      title: setup.title,
      managerId: setup.managerId,
      participantId: setup.participantId,
      employeeId: setup.participantId,
      workflowType: setup.workflowType,
      frequency: setup.frequency,
      schedulePattern: setup.schedulePattern ?? setup.frequency ?? null,
      duration: setup.duration,
      defaultAgenda: setup.defaultAgenda,
      questions: setup.questions,
      isActive: setup.isActive,
      startDate: setup.startDate ?? setup.nextScheduledDate ?? null,
      startTime: setup.startTime ?? null,
      customCron: setup.customCron ?? null,
      nextScheduledDate: setup.nextScheduledDate ?? null,
      completionDueDays: setup.emailDueDays ?? null,
      managerProfile: deriveProfile(setup.manager),
      participantProfile: deriveProfile(setup.participant),
    }
  }, [setup])

  return (
    <CreateOneOnOneSetupModal
      isOpen={isOpen}
      onClose={onClose}
      teamId={teamId}
      teamMembers={teamMembers}
      onSuccess={onSuccess}
      mode="edit"
      initialSetup={initialSetup}
    />
  )
}
